// Single-currency accounts. A GBP account holds UK (+44) numbers.
// A USD account holds US (+1) numbers. The other pairing is refused.
// Deno-free so Vitest and the React app can import it.

export type AccountCurrency = "gbp" | "usd";

export const ACCOUNT_CURRENCY_MISMATCH = "account_currency_mismatch";

export function normalizeAccountCurrency(value: unknown): AccountCurrency {
  return typeof value === "string" && value.trim().toLowerCase() === "usd" ? "usd" : "gbp";
}

/** Currency of a phone line. Digits win over the stored country code. Other countries are null. */
export function currencyForPhoneLine(phone?: string | null, countryCode?: string | null): AccountCurrency | null {
  let digits = (phone || "").replace(/\D/g, "");
  if (digits.startsWith("00")) digits = digits.slice(2);
  if (digits.startsWith("44")) return "gbp";
  if (digits.startsWith("1")) return "usd";
  const code = (countryCode || "").trim().toUpperCase();
  if (code === "GB" || code === "UK") return "gbp";
  if (code === "US" || code === "CA") return "usd";
  return null;
}

export function currencyMismatchMessage(accountCurrency: AccountCurrency, lineCurrency: AccountCurrency): string {
  if (accountCurrency === "gbp" && lineCurrency === "usd") {
    return "US numbers (+1) are billed in USD and need a separate US account. This account is billed in GBP. Create a US account, then buy or assign the +1 number there.";
  }
  if (accountCurrency === "usd" && lineCurrency === "gbp") {
    return "UK numbers (+44) are billed in GBP and need a separate UK account. This account is billed in USD. Create a UK account, then buy or assign the +44 number there.";
  }
  return "";
}

export function assertNumberCurrency(input: {
  accountCurrency: AccountCurrency;
  phone?: string | null;
  countryCode?: string | null;
  /** Internal accounts may hold +44 and +1 on the same account. */
  billingExempt?: boolean;
}):
  | { ok: true; lineCurrency: AccountCurrency | null }
  | { ok: false; code: typeof ACCOUNT_CURRENCY_MISMATCH; message: string; lineCurrency: AccountCurrency } {
  const lineCurrency = currencyForPhoneLine(input.phone, input.countryCode);
  if (input.billingExempt === true || !lineCurrency || lineCurrency === input.accountCurrency) {
    return { ok: true, lineCurrency };
  }
  return {
    ok: false,
    code: ACCOUNT_CURRENCY_MISMATCH,
    lineCurrency,
    message: currencyMismatchMessage(input.accountCurrency, lineCurrency),
  };
}

export function accountCurrencyNotice(currency: AccountCurrency): string {
  if (currency === "usd") {
    return "This account is billed in USD ($). It can hold US (+1) numbers. A UK (+44) number needs a separate UK account billed in GBP.";
  }
  return "This account is billed in GBP (£). It can hold UK (+44) numbers. A US (+1) number needs a separate US account billed in USD.";
}

/** Draft clause for the Terms page. Not in force until Phillip and Legal approve it. */
export const TERMS_CURRENCY_DRAFT_HEADING = "Phone numbers and account currency";

export const TERMS_CURRENCY_DRAFT = [
  "DRAFT — for Phillip and Legal review. This wording is not in force and does not replace the Terms of Service at https://speak.ad/terms until Legal approves it.",
  "Each Speak account is billed in a single currency.",
  "A GBP account is billed in pounds sterling and holds UK phone numbers (+44). A USD account is billed in US dollars and holds US phone numbers (+1).",
  "Speak refuses a US (+1) number on a GBP account. To use a +1 number, create a separate US account billed in USD and buy or assign the number on that account.",
  "Speak refuses a UK (+44) number on a USD account. To use a +44 number, create a separate UK account billed in GBP and buy or assign the number on that account.",
  "Usage beyond the included call credit is billed in the account currency: £12.21 per hour on a GBP account and $15.50 per hour on a USD account. Compliance Plus (Mneme), when enabled, is £16.50/hr + VAT on a GBP account and includes talk time. It is not charged on top of that talk-time rate. The app does not add VAT. A USD Compliance Plus rate is not confirmed.",
].join(" ");
